import type { ReactNode } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type {
  CollaboratorPermission,
  Institution,
  InstitutionCollaborator,
} from '@/types'
import {
  extractInstitutionFromMe,
  fetchAdminMe,
  fetchDonorMe,
  fetchInstitutionMe,
  loginAdmin as loginAdminRequest,
  loginDonor as loginDonorRequest,
  loginInstitution,
  logoutAdmin,
  logoutDonor,
  logoutInstitution,
  registerInstitution,
  verifyAdminTwoFactorLogin,
  verifyInstitutionTwoFactorLogin,
  type RegisterInstitutionInput,
} from '@/lib/auth'
import {
  extractManagerialDashboardAccessFromMe,
  type ManagerialDashboardAccess,
} from '@/lib/managerial-dashboard-access'
import type { AdminLoginOutcome, InstitutionLoginOutcome } from '@/types/two-factor'
import { clearSelectedInstitutionId } from '@/lib/api/client'
import type { AuthGuard } from '@/lib/jwt'

interface AuthContextValue {
  institution: Institution | null
  collaborator: InstitutionCollaborator | null
  permissions: CollaboratorPermission[]
  isCollaborator: boolean
  managerialDashboardAccess: ManagerialDashboardAccess | null
  token: string | null
  guard: AuthGuard | null
  me: unknown | null
  isLoading: boolean
  hasPermission: (permission: CollaboratorPermission) => boolean
  register: (input: RegisterInstitutionInput) => Promise<Institution>
  login: (input: { email: string; password: string }) => Promise<InstitutionLoginOutcome>
  loginAdmin: (input: { email: string; password: string }) => Promise<AdminLoginOutcome>
  completeTwoFactorLogin: (input: {
    challengeToken: string
    code: string
  }) => Promise<Institution>
  completeAdminTwoFactorLogin: (input: {
    challengeToken: string
    code: string
  }) => Promise<unknown>
  loginDonor: (input: { email: string; password: string }) => Promise<unknown>
  refreshMe: (nextToken?: string | null) => Promise<Institution | unknown | null>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const SESSION_GUARD_KEY = 'legalprime:sessionGuard'
const SESSION_MARKER = 'session'

function readSessionGuard(): AuthGuard | null {
  if (typeof window === 'undefined') return null
  const value = window.localStorage.getItem(SESSION_GUARD_KEY)
  if (value === 'admin' || value === 'institution' || value === 'donor') {
    return value
  }
  return null
}

function writeSessionGuard(guard: AuthGuard | null): void {
  if (typeof window === 'undefined') return
  if (guard) {
    window.localStorage.setItem(SESSION_GUARD_KEY, guard)
    return
  }
  window.localStorage.removeItem(SESSION_GUARD_KEY)
}

function permissionsFromCollaborator(
  collaborator: InstitutionCollaborator | null | undefined
): CollaboratorPermission[] {
  return collaborator?.permissions ?? []
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() =>
    readSessionGuard() ? SESSION_MARKER : null
  )
  const [guard, setGuard] = useState<AuthGuard | null>(() => readSessionGuard())
  const [institution, setInstitution] = useState<Institution | null>(null)
  const [collaborator, setCollaborator] = useState<InstitutionCollaborator | null>(null)
  const [permissions, setPermissions] = useState<CollaboratorPermission[]>([])
  const [managerialDashboardAccess, setManagerialDashboardAccess] =
    useState<ManagerialDashboardAccess | null>(null)
  const [me, setMe] = useState<unknown | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const clearLocalSession = useCallback(() => {
    setInstitution(null)
    setCollaborator(null)
    setPermissions([])
    setManagerialDashboardAccess(null)
    setMe(null)
    setGuard(null)
    setToken(null)
    writeSessionGuard(null)
  }, [])

  const applyAuthenticated = useCallback((nextGuard: AuthGuard) => {
    setGuard(nextGuard)
    setToken(SESSION_MARKER)
    writeSessionGuard(nextGuard)
  }, [])

  const refreshMe = useCallback(
    async (nextToken?: string | null, sessionGuard?: AuthGuard) => {
      const requestedGuard =
        nextToken === null ? null : sessionGuard ?? guard ?? readSessionGuard()

      if (nextToken === null || requestedGuard === null) {
        clearLocalSession()
        return null
      }

      if (requestedGuard === 'institution') {
        const response = await fetchInstitutionMe()
        const current = extractInstitutionFromMe(response)
        setInstitution(current)
        setCollaborator(response.data?.collaborator ?? null)
        setPermissions(permissionsFromCollaborator(response.data?.collaborator))
        setManagerialDashboardAccess(extractManagerialDashboardAccessFromMe(response))
        setMe(response)
        applyAuthenticated('institution')
        return current
      }

      if (requestedGuard === 'admin') {
        const response = await fetchAdminMe()
        setInstitution(null)
        setCollaborator(null)
        setPermissions([])
        setManagerialDashboardAccess(null)
        setMe(response)
        applyAuthenticated('admin')
        return response
      }

      const response = await fetchDonorMe()
      setInstitution(null)
      setCollaborator(null)
      setPermissions([])
      setManagerialDashboardAccess(null)
      setMe(response)
      applyAuthenticated('donor')
      return response
    },
    [applyAuthenticated, clearLocalSession, guard]
  )

  useEffect(() => {
    let cancelled = false

    const boot = async () => {
      const hintedGuard = readSessionGuard()
      if (!hintedGuard) {
        setIsLoading(false)
        return
      }

      try {
        setGuard(hintedGuard)
        await refreshMe(SESSION_MARKER)
      } catch {
        if (!cancelled) {
          clearLocalSession()
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    void boot()
    return () => {
      cancelled = true
    }
    // Restore once from the httpOnly cookie + guard hint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const register = useCallback(
    async (input: RegisterInstitutionInput) => {
      const response = await registerInstitution(input)
      if (!response.success || !response.data?.institution) {
        throw new Error('Falha ao registrar instituição.')
      }
      clearSelectedInstitutionId()
      applyAuthenticated('institution')
      setInstitution(response.data.institution)
      setCollaborator(null)
      setPermissions([])
      setManagerialDashboardAccess(null)
      setMe(response)
      try {
        const refreshed = await refreshMe(SESSION_MARKER, 'institution')
        if (refreshed && typeof refreshed === 'object' && 'id' in refreshed) {
          return refreshed as Institution
        }
      } catch {
        // Mantém dados do registro se /me falhar momentaneamente.
      }
      return response.data.institution
    },
    [applyAuthenticated, refreshMe]
  )

  const applyInstitutionSession = useCallback(
    async (
      institutionProfile: Institution,
      nextCollaborator: InstitutionCollaborator | null | undefined,
      meResponse: unknown
    ): Promise<Institution> => {
      clearSelectedInstitutionId()
      applyAuthenticated('institution')
      setInstitution(institutionProfile)
      setCollaborator(nextCollaborator ?? null)
      setPermissions(permissionsFromCollaborator(nextCollaborator))
      setManagerialDashboardAccess(extractManagerialDashboardAccessFromMe(meResponse))
      setMe(meResponse)
      try {
        const refreshed = await refreshMe(SESSION_MARKER, 'institution')
        if (refreshed && typeof refreshed === 'object' && 'id' in refreshed) {
          return refreshed as Institution
        }
      } catch {
        // Mantém dados do login se /me falhar momentaneamente.
      }
      return institutionProfile
    },
    [applyAuthenticated, refreshMe]
  )

  const login = useCallback(
    async (input: {
      email: string
      password: string
    }): Promise<InstitutionLoginOutcome> => {
      const response = await loginInstitution(input)
      if (response.data?.requires_two_factor && response.data.challenge_token) {
        return {
          status: 'two_factor_required',
          challengeToken: response.data.challenge_token,
        }
      }
      if (!response.success || !response.data?.institution) {
        throw new Error('Falha ao autenticar.')
      }
      await applyInstitutionSession(
        response.data.institution,
        response.data.collaborator,
        response
      )
      return { status: 'authenticated' }
    },
    [applyInstitutionSession]
  )

  const completeTwoFactorLogin = useCallback(
    async (input: { challengeToken: string; code: string }): Promise<Institution> => {
      const response = await verifyInstitutionTwoFactorLogin({
        challenge_token: input.challengeToken,
        code: input.code,
      })
      if (!response.success || !response.data?.institution) {
        throw new Error('Não foi possível validar o código.')
      }
      return applyInstitutionSession(
        response.data.institution,
        response.data.collaborator,
        response
      )
    },
    [applyInstitutionSession]
  )

  const applyAdminSession = useCallback(async (): Promise<unknown> => {
    clearSelectedInstitutionId()
    applyAuthenticated('admin')
    setInstitution(null)
    setCollaborator(null)
    setPermissions([])
    setManagerialDashboardAccess(null)
    const meResponse = await fetchAdminMe()
    setMe(meResponse)
    return meResponse
  }, [applyAuthenticated])

  const loginAdmin = useCallback(
    async (input: { email: string; password: string }): Promise<AdminLoginOutcome> => {
      const response = await loginAdminRequest(input)
      if (response.data?.requires_two_factor && response.data.challenge_token) {
        return {
          status: 'two_factor_required',
          challengeToken: response.data.challenge_token,
        }
      }
      if (!response.success) {
        throw new Error('Falha ao autenticar admin.')
      }
      await applyAdminSession()
      return { status: 'authenticated' }
    },
    [applyAdminSession]
  )

  const completeAdminTwoFactorLogin = useCallback(
    async (input: { challengeToken: string; code: string }): Promise<unknown> => {
      const response = await verifyAdminTwoFactorLogin({
        challenge_token: input.challengeToken,
        code: input.code,
      })
      if (!response.success) {
        throw new Error('Não foi possível validar o código.')
      }
      return applyAdminSession()
    },
    [applyAdminSession]
  )

  const loginDonor = useCallback(async (input: { email: string; password: string }) => {
    const response = await loginDonorRequest(input)
    if (!response.success) {
      throw new Error('Falha ao autenticar donor.')
    }
    applyAuthenticated('donor')
    setInstitution(null)
    setCollaborator(null)
    setPermissions([])
    setManagerialDashboardAccess(null)
    const meResponse = await fetchDonorMe()
    setMe(meResponse)
    return meResponse
  }, [applyAuthenticated])

  const signOutInFlight = useRef<Promise<void> | null>(null)
  const signOut = useCallback(() => {
    if (signOutInFlight.current) {
      return signOutInFlight.current
    }

    const pending = Promise.allSettled([
      logoutAdmin(),
      logoutInstitution(),
      logoutDonor(),
    ]).then(() => {
      clearSelectedInstitutionId()
      clearLocalSession()
      signOutInFlight.current = null
    })

    signOutInFlight.current = pending
    return pending
  }, [clearLocalSession])

  const isCollaborator = Boolean(collaborator)
  const hasPermission = useCallback(
    (permission: CollaboratorPermission) => {
      if (!isCollaborator) return true
      return permissions.includes(permission)
    },
    [isCollaborator, permissions]
  )

  const value = useMemo(
    () => ({
      institution,
      collaborator,
      permissions,
      isCollaborator,
      managerialDashboardAccess,
      token,
      guard,
      me,
      isLoading,
      hasPermission,
      register,
      login,
      loginAdmin,
      completeTwoFactorLogin,
      completeAdminTwoFactorLogin,
      loginDonor,
      refreshMe,
      signOut,
    }),
    [
      institution,
      collaborator,
      permissions,
      isCollaborator,
      managerialDashboardAccess,
      token,
      guard,
      me,
      isLoading,
      hasPermission,
      register,
      login,
      loginAdmin,
      completeTwoFactorLogin,
      completeAdminTwoFactorLogin,
      loginDonor,
      refreshMe,
      signOut,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/** Hook para acessar o contexto de autenticação. */
// eslint-disable-next-line react-refresh/only-export-components -- hook exportado junto ao provider (padrão de contexto)
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}
